/**
 * FullscreenButton — bascule plein-écran dans la TopNav.
 *
 * - Icône Maximize2 (mode normal) / Minimize2 (mode plein-écran)
 * - Toggle via Fullscreen API : document.documentElement.requestFullscreen() / exitFullscreen()
 * - Écoute `fullscreenchange` pour synchroniser l'état localement
 *
 * FEAT-FULLSCREEN-TOGGLE
 */
import { useT } from '@/lib/i18n';
import { useState, useEffect } from 'react';
import { Maximize2, Minimize2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export function FullscreenButton() {
  const t = useT();
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleChange);
    // sync on mount (rare : si déjà en fullscreen à l'initialisation)
    setIsFullscreen(!!document.fullscreenElement);
    return () => document.removeEventListener('fullscreenchange', handleChange);
  }, []);

  const toggle = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch {
      // Le navigateur peut refuser (politiques sandbox, etc.)
    }
  };

  const label = t(isFullscreen ? 'commun.quitterPleinEcran' : 'commun.pleinEcran');

  return (
    <button
      type="button"
      onClick={() => void toggle()}
      title={label}
      aria-label={label}
      className={cn(
        'flex h-7 w-7 items-center justify-center rounded-md transition-colors',
        'text-muted-foreground hover:bg-[var(--surface-hover,var(--surface-border))] hover:text-foreground',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-samsung-500/50',
      )}
    >
      {isFullscreen ? (
        <Minimize2 size={15} className="shrink-0" aria-hidden="true" />
      ) : (
        <Maximize2 size={15} className="shrink-0" aria-hidden="true" />
      )}
    </button>
  );
}
